import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { Grid, Button, Tooltip } from '@material-ui/core';
import { AssignmentTurnedIn, ArrowUpward } from '@material-ui/icons';
import { BackButton, ButtonLink, Panel, Container } from '../../../../../../Components';
import { FormValues } from '../../../../../../Components/Form/Form.types';
import { CompanyUnitEditForm, ICompanyUnit } from '../../CompanyUnits.Edit';
import { LawProcesses } from './LawProcesses';
import { LawAspects } from './LawAspects';
import { LawTags } from './LawTags';
import { LawHeader } from '../../LawHeader';
import { ICustomerLaw } from '../CustomerLaws.List';
import { UserContext } from '../../../../../../Context/UserContext/UserContext';
import { AptorApi, useAptorApi, useFetchSelectOptions } from '../../../../../../Api';
import { useSnackbar } from 'notistack';
import { useStyles } from "../../../../AdministrationModule.styles";

const LawCompanyUnitForm = (props: { lawId: number; isNew?: boolean; unitId?: number }) => {
  const { lawId, isNew, unitId } = props;
  const { api, abortController } = useAptorApi();
  const navigate = useNavigate();
  const location = useLocation();
  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const { getCompanyUnitName } = useContext(UserContext);
  const lawPath = location.pathname.startsWith('/law-portal/settings')
    ? `/law-portal/settings/legal-list/${lawId}`
    : `/organization/law-portal/laws/${lawId}`;
  const [units] = useFetchSelectOptions(
    useCallback(() => api.getCustomerLawCompanyUnits(lawId), [lawId, api]),
    abortController,
  );
  const [users] = useFetchSelectOptions(
    useCallback(() => {
      if (isNew) return api.getCustomerUsers();
      return api.getCustomerCompanyUnitUsers(unitId!);
    }, [isNew, unitId, api]),
    abortController,
  );
  const fetcher = useCallback(() => api.getLawCompanyUnit<ICompanyUnit>(lawId, unitId!), [lawId, unitId, api]);

  const handleSubmit = async (data: FormValues, api: AptorApi) => {
    var ok = true;
    if (isNew) {
      ok = await api.addLawCompanyUnits(lawId, data);
    } else await api.updateLawCompanyUnit(lawId, unitId!, { complianceUsers: data.complianceUsers });
    if (!ok && !abortController.current.signal.aborted)
      enqueueSnackbar(
        formatMessage({ id: 'organization.user-unit-mismatch' }, { alias: getCompanyUnitName(formatMessage, true) }),
        { variant: 'warning' },
      );
    navigate(lawPath);
  };

  return (
    <CompanyUnitEditForm
      isNew={!!isNew}
      units={units}
      users={users}
      dataFetcher={fetcher}
      handleSubmit={handleSubmit}
    />
  );
};

export const AddLawCompanyUnit = () => {
  const params = useParams<{ lawid: string }>();
  const location = useLocation();
  const id = parseInt(params.lawid!);
  const backLink = location.pathname.startsWith('/law-portal/settings')
    ? `/law-portal/settings/legal-list/${id}`
    : `/organization/law-portal/laws/${id}`;
  return (
    <Container>
      <BackButton link={backLink} />
      <LawCompanyUnitForm lawId={id} isNew />
    </Container>
  );
};

export const EditLawCompanyUnit = () => {
  const params = useParams<{ lawid: string; unitid: string }>();
  const lawid = parseInt(params.lawid!);
  const unitid = parseInt(params.unitid!);
  const { formatMessage } = useIntl();
  const { api, abortController } = useAptorApi();
  const location = useLocation();
  const [name, setName] = useState('');
  const classes = useStyles();
  const backLink = location.pathname.startsWith('/law-portal/settings')
    ? `/law-portal/settings/legal-list/${lawid}`
    : `/organization/law-portal/laws/${lawid}`;
  const checklistLink = `${backLink}/company-units/${unitid}/checklist`;

  useEffect(() => {
    const initializeName = async () => {
      const law = await api.getCustomerLaw<ICustomerLaw>(lawid);
      if (abortController.current.signal.aborted) {
        return;
      }
      setName(law.number + ' ' + law.name);
    };
    if (name === '') {
      initializeName();
    }
  }, [lawid, name, api, abortController]);

  const [showScrollTop, setShowScrollTop] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 100) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <>
      <Container>
        <BackButton link={backLink} />
        <Grid container spacing={4} direction="column">
          <Grid item>
            <LawHeader name={name} />
          </Grid>
          <Grid item>
            <ButtonLink
              to={checklistLink}
              text={formatMessage({ id: 'law-portal.manage.edit-law.lawChecklist' })}
              icon={<AssignmentTurnedIn />}
            />
          </Grid>
          <Grid item>
            <LawCompanyUnitForm lawId={lawid} unitId={unitid} />
          </Grid>
          <Grid item>
            <Panel titleKey="law-portal.manage.edit-law.connectedProcesses" accordion>
              <LawProcesses lawid={lawid} unitid={unitid} />
            </Panel>
          </Grid>
          <Grid item>
            <Panel titleKey="law-portal.manage.edit-law.connectedAspects" accordion>
              <LawAspects lawid={lawid} unitid={unitid} />
            </Panel>
          </Grid>
          <Grid item>
            <Panel titleKey="law-portal.manage.edit-law.connectedTags" accordion>
              <LawTags lawid={lawid} unitid={unitid} />
            </Panel>
          </Grid>
        </Grid>
      </Container>
      {showScrollTop && (
        <Tooltip title={formatMessage({ id: "bottom.to.top.btn.tooltip.text", defaultMessage: "Back to top" })}>
          <Button
            onClick={() => {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className={classes.topButton}
          >
            <ArrowUpward style={{ fontSize: 20 }} />
          </Button>
        </Tooltip>
      )}
    </>
  );
};
